import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { isCancelled } from '@reditelji/shared';
import { useState } from 'react';
import { useMailBatch } from '../api/mail';
import { useSession } from '../api/session';
import { useVolunteerMutations, type VolunteerRow } from '../api/volunteers';
import { DailyDigestCard } from '../components/DailyDigestCard';
import { ago, plDni } from '../lib/ui';
import type { VolunteerRollup } from './derive';
import { hoursLabel } from './derive';
import { CARD, MONO, SECTION_LABEL } from './ui';

export function EmptyState({
  rows,
  openPerson,
}: {
  /** Everyone, somebody not coming included (SPEC §1.1K). */
  rows: { v: VolunteerRow; r: VolunteerRollup }[];
  openPerson: (id: string, tab?: 'profil' | 'komunikacija') => void;
}) {
  const mut = useVolunteerMutations();
  const needBrief = rows.filter(
    ({ v, r }) => r.shifts.length > 0 && r.briefStale && v.email && !isCancelled(v),
  );
  const [checked, setChecked] = useState<Set<string> | null>(null);
  // Watching the batch is what refreshes this screen once the mails land: the
  // hook invalidates the roster and the send states when it reports done.
  const [watching, setWatching] = useState<string | null>(null);
  useMailBatch(watching);
  const sel = checked ?? new Set(needBrief.map((x) => x.v.id));
  // Somebody not coming is not chased, but what they wrote — "mogoče pa
  // vseeno pridem?" — still waits for an answer (`commsTurn` says both).
  const stale = rows.filter(({ r }) => r.awaitingUs || r.noReply);
  const { can } = useSession();

  // The digest, the bulk napotnica and the unanswered threads are all
  // correspondence (SPEC §1.1L); without it the roster is the whole screen.
  if (!can('comms:read')) {
    return (
      <div style={{ padding: '18px 20px 40px' }}>
        <div style={{ ...CARD, padding: 16, fontSize: rem(13), color: 'oklch(0.55 0.012 90)' }}>
          Izberi prostovoljca na levi za profil in razpored.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '18px 20px 40px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 16, alignItems: 'start' }}>
        {/* Dnevni povzetek (shared with Pregled) */}
        <DailyDigestCard
          footer={
            <div style={{ marginTop: 14, fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>
              Izberi prostovoljca na levi za profil, pogovor in zgodovino sprememb.
            </div>
          }
        />

        {/* right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <div style={{ ...CARD, padding: 14 }}>
            <div style={{ ...SECTION_LABEL, marginBottom: 10 }}>MNOŽIČNO POŠILJANJE NAVODIL</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
              <button
                onClick={() =>
                  setChecked(sel.size === needBrief.length ? new Set() : new Set(needBrief.map((x) => x.v.id)))
                }
                style={{
                  cursor: 'pointer',
                  padding: '5px 10px',
                  borderRadius: 6,
                  border: '1px solid oklch(0.9 0.006 95)',
                  background: 'oklch(0.985 0.004 95)',
                  fontSize: rem(12),
                  fontFamily: 'inherit',
                  color: 'oklch(0.38 0.012 90)',
                }}
              >
                {sel.size === needBrief.length ? 'Odznači vse' : 'Izberi vse'}
              </button>
              <span style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)' }}>
                {needBrief.length} oseb potrebuje navodila
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 218, overflow: 'auto' }}>
              {needBrief.map(({ v, r }) => {
                const on = sel.has(v.id);
                return (
                  <div
                    key={v.id}
                    onClick={() => {
                      const next = new Set(sel);
                      if (on) next.delete(v.id);
                      else next.add(v.id);
                      setChecked(next);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '7px 9px',
                      borderRadius: 7,
                      cursor: 'pointer',
                      background: on ? 'oklch(0.985 0.012 155)' : 'oklch(1 0 0)',
                      border: `1px solid ${on ? 'oklch(0.89 0.035 155)' : 'oklch(0.94 0.005 95)'}`,
                    }}
                  >
                    <span
                      style={{
                        width: 15,
                        height: 15,
                        borderRadius: 4,
                        background: on ? 'oklch(0.62 0.09 155)' : 'oklch(1 0 0)',
                        border: `1px solid ${on ? 'oklch(0.62 0.09 155)' : 'oklch(0.85 0.008 95)'}`,
                        color: 'oklch(1 0 0)',
                        fontSize: rem(11),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {on ? '✓' : ''}
                    </span>
                    <span style={{ fontSize: rem(13), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.name}
                    </span>
                    <span style={{ fontFamily: MONO, fontSize: rem(11), color: 'oklch(0.6 0.012 90)', flexShrink: 0 }}>
                      {r.shifts.length} · {hoursLabel(r.minutes)}
                    </span>
                  </div>
                );
              })}
            </div>
            {needBrief.length === 0 ? (
              <div style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}>
                Vsi prostovoljci z izmenami imajo aktualna navodila.
              </div>
            ) : null}
            <button
              disabled={sel.size === 0 || mut.sendBriefings.isPending}
              onClick={() =>
                mut.sendBriefings.mutate([...sel], {
                  onSuccess: (batch) => {
                    // The mails land minutes later in a worker, so the roster
                    // here would keep reading "še ni bilo poslano" until some
                    // unrelated mutation refreshed it — long enough for someone
                    // to send the same people a second time.
                    setWatching(batch.queued ? batch.id : null);
                    // The batch outlives this click; the Prejemniki view on
                    // Sporočila is where its progress and report live.
                    notifications.show({
                      message: batch.queued
                        ? `Pošiljanje ${batch.counts.total} napotnic se je začelo. Potek je na zavihku Sporočila.`
                        : `Napotnice poslane ${batch.counts.sent} osebam${
                            batch.counts.total - batch.counts.sent
                              ? `, izpuščenih ${batch.counts.total - batch.counts.sent}`
                              : ''
                          }.`,
                    });
                    setChecked(new Set());
                  },
                })
              }
              style={{
                width: '100%',
                marginTop: 10,
                cursor: sel.size ? 'pointer' : 'default',
                padding: 9,
                borderRadius: 7,
                border: `1px solid ${sel.size ? 'oklch(0.55 0.09 155)' : 'oklch(0.9 0.006 95)'}`,
                background: sel.size ? 'oklch(0.62 0.09 155)' : 'oklch(0.95 0.005 95)',
                fontSize: rem(13),
                fontWeight: 600,
                fontFamily: 'inherit',
                color: sel.size ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
              }}
            >
              {mut.sendBriefings.isPending ? 'Začenjam…' : `Pošlji navodila (${sel.size})`}
            </button>
          </div>

          <div style={{ ...CARD, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
              <span style={SECTION_LABEL}>BREZ ODGOVORA</span>
              <span style={{ fontFamily: MONO, fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>{stale.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {stale.map(({ v, r }) => (
                <div
                  key={v.id}
                  onClick={() => openPerson(v.id, 'komunikacija')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '7px 9px',
                    borderRadius: 7,
                    cursor: 'pointer',
                    background: 'oklch(1 0 0)',
                    border: '1px solid oklch(0.93 0.03 25)',
                  }}
                >
                  <span style={{ fontSize: rem(13), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.name}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: rem(11), color: 'oklch(0.52 0.11 25)', flexShrink: 0 }}>
                    {r.awaitingUs ? `čaka naš odgovor · ${ago(r.waitDays)}` : `brez odgovora ${plDni(r.waitDays)}`}
                  </span>
                </div>
              ))}
            </div>
            {stale.length === 0 ? (
              <div style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}>Vsi pogovori so aktualni.</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
